/**
 * ReverbModule — convolution reverb at the end of the effect chain.
 *
 * Graph:
 *
 *   input ─┬─ dryGain ────────────────────────────────► outputMix ─►
 *          └─► convolver ─► dampFilter ─► wetGain ────► outputMix
 *
 * The impulse response is synthesized from decaying noise with the Size
 * parameter controlling decay time. Damp is a lowpass on the wet path —
 * rolls off highs to simulate absorptive room surfaces. Mix is a standard
 * equal-power crossfade (well, same pattern as Distortion/Delay/Crush —
 * linear for consistency with the rest of the effects).
 *
 * IR generation happens on Size change. A short debounce guards the
 * scheduler so dragging the knob doesn't regenerate N times per second —
 * 100ms is inaudible for reverb tails and keeps the CPU cool.
 */

import type { AudioEngine } from '../AudioEngine';
import {
  NumericParameter,
  type AnyParameter,
} from '../../params/Parameter';
import type { ModuleState, SynthModule } from './SynthModule';
import { mintId, readParamState, writeParamState } from './util';
import { createPitchShifter, type PitchShifter } from '../pitchShifter';

/** Time to wait after a Size change before regenerating the IR. */
const IR_DEBOUNCE_MS = 100;
/** Hard ceiling on reverb tail so an extreme Size doesn't allocate gigs of RAM. */
const MAX_SIZE_SEC = 8;
/**
 * Shimmer regeneration amount when Pitch is active. The tap is
 * taken *pre-damp* so the lowpass only colors the audible output,
 * not the regen path — that lets the high-end of each cascade
 * survive into the next loop. Capped at 0.7: the convolver's IR
 * decays exponentially, so this multiplier stays well inside the
 * stability window even with the longest Size.
 *
 * Higher values = more cascade levels audible before the loop
 * fades out. 0.7 gives roughly 4–5 perceptible "rungs" up the
 * pitch ladder before the tail loses energy.
 */
const SHIMMER_FEEDBACK = 0.7;

export class ReverbModule implements SynthModule {
  readonly kind = 'signal' as const;
  readonly type = 'reverb' as const;
  readonly displayName = 'REVERB';
  readonly id: string;
  bypass = false;

  readonly size = new NumericParameter({
    name: 'Size',
    default: 1.8,
    min: 0.1,
    max: MAX_SIZE_SEC,
    curve: 'exp',
    unit: 's',
    smoothingMs: 0, // discrete IR swaps, no param smoothing
    // Strongly low bias — large random rooms drown patches.
    randomSkew: 0.4,
    formatter: (v) => (v < 1 ? `${(v * 1000).toFixed(0)}ms` : `${v.toFixed(2)}s`),
  });

  readonly damp = new NumericParameter({
    name: 'Damp',
    default: 6000,
    min: 500,
    max: 20000,
    curve: 'exp',
    unit: 'Hz',
    smoothingMs: 20,
    randomSkew: 1.5,
    formatter: (v) =>
      v >= 1000 ? `${(v / 1000).toFixed(1)}k` : `${v.toFixed(0)}`,
  });

  readonly mix = new NumericParameter({
    name: 'Mix',
    default: 0.25,
    min: 0,
    max: 1,
    curve: 'linear',
    smoothingMs: 20,
    // Low bias — full-wet reverb is rarely the intent.
    randomSkew: 0.6,
    formatter: (v) => `${Math.round(v * 100)}%`,
  });
  /**
   * Shimmer pitch shift, in semitones. When non-zero, a portion of
   * the wet tail is pitched by this amount and fed back into the
   * convolver's input — each loop runs the IR over the previously
   * shifted signal, producing the cascading "halo" cloud. Set to
   * 0 to bypass entirely (regen path muted, plain convolution
   * reverb). Classic settings: +12 (Eventide / Strymon shimmer),
   * -12 (sub-octave doom), +7 (cascading fifths).
   */
  readonly pitch = new NumericParameter({
    name: 'Pitch',
    default: 0,
    min: -24,
    max: 24,
    curve: 'linear',
    smoothingMs: 0,
    randomSkew: 1,
    formatter: (v) => {
      const n = Math.round(v);
      if (n === 0) return 'OFF';
      return `${n > 0 ? '+' : ''}${n} st`;
    },
  });

  readonly parameters: readonly AnyParameter[] = [
    this.size,
    this.damp,
    this.mix,
    this.pitch,
  ];

  private engine: AudioEngine | null = null;
  private inputNode: GainNode | null = null;
  private outputMixNode: GainNode | null = null;
  /** Mixer feeding the convolver. Sums the dry input with the
   *  shimmer regen path so a single ConvolverNode handles both. */
  private reverbIn: GainNode | null = null;
  private convolver: ConvolverNode | null = null;
  private dampFilter: BiquadFilterNode | null = null;
  private dryGain: GainNode | null = null;
  private wetGain: GainNode | null = null;
  /** Granular pitch shifter on the regenerative wet tap. */
  private pitchShifter: PitchShifter | null = null;
  /** Gain on the shimmer feedback path — 0 when Pitch is OFF, fixed
   *  feedback amount when active. Crossfaded so toggling pitch
   *  doesn't click. */
  private shimmerGain: GainNode | null = null;
  private started = false;
  private readonly unsubs: Array<() => void> = [];
  private irTimer: ReturnType<typeof setTimeout> | null = null;

  constructor(id?: string) {
    this.id = id ?? mintId('rev');
  }

  get input(): AudioNode | null {
    return this.inputNode;
  }
  get output(): AudioNode | null {
    return this.outputMixNode;
  }

  init(engine: AudioEngine): void {
    if (this.started) return;
    this.engine = engine;
    const ctx = engine.context;

    this.inputNode = ctx.createGain();
    this.outputMixNode = ctx.createGain();
    this.reverbIn = ctx.createGain();
    this.convolver = ctx.createConvolver();
    this.convolver.normalize = true;
    this.dampFilter = ctx.createBiquadFilter();
    this.dampFilter.type = 'lowpass';
    this.dampFilter.frequency.value = this.damp.value;
    this.dryGain = ctx.createGain();
    this.wetGain = ctx.createGain();
    this.pitchShifter = createPitchShifter(ctx);
    this.shimmerGain = ctx.createGain();
    this.shimmerGain.gain.value = 0;

    // Dry passes straight through.
    this.inputNode.connect(this.dryGain);
    this.dryGain.connect(this.outputMixNode);

    // Wet path. The convolver sees a sum of the dry input AND the
    // shimmer regen path (when Pitch is active). This lets a single
    // IR handle both the initial reverb and the cascading pitched
    // tails — each pass runs the previously-shifted signal through
    // the same impulse response again.
    //
    //   input ─► reverbIn ─► convolver ─┬─► dampFilter ─► wetGain ─► out
    //                ▲                  │
    //                └── shimmerGain ◄── pitchShifter ◄┘
    //
    // Shimmer tap is pre-damp (off the convolver directly) so the
    // lowpass only colors the audible output, not the regen loop.
    // That preserves high-frequency content across multiple
    // cascade levels — without this, the second loop already
    // sounds muffled and the pitched climb dies on rung 2.
    this.inputNode.connect(this.reverbIn);
    this.reverbIn.connect(this.convolver);
    this.convolver.connect(this.dampFilter);
    this.dampFilter.connect(this.wetGain);
    this.wetGain.connect(this.outputMixNode);
    this.convolver.connect(this.pitchShifter.input);
    this.pitchShifter.output.connect(this.shimmerGain);
    this.shimmerGain.connect(this.reverbIn);

    this.damp.bindAudioParam(this.dampFilter.frequency, ctx);

    this.regenerateIR();
    this.applyMix(this.mix.value, true);
    // Apply the initial pitch (probably 0/OFF on a fresh patch).
    this.pitchShifter.setPitchSemitones(this.pitch.value);
    this.applyShimmerGain(this.pitch.value, /*immediate*/ true);

    this.unsubs.push(this.mix.subscribe((v) => this.applyMix(v)));
    this.unsubs.push(this.size.subscribe(() => this.scheduleIRRegen()));
    // subscribeDisplay so audio-rate mod sources (LFO, S&H,
    // sequencer, mod wheel) actually drive the shifter when routed
    // to REVERB · Pitch. `subscribe` only fires on UI / preset
    // writes; the mod router's rAF sampler updates Parameters via
    // `setModContribution`, which fires the display channel.
    this.unsubs.push(
      this.pitch.subscribeDisplay((v) => {
        this.pitchShifter?.setPitchSemitones(v);
        this.applyShimmerGain(v);
      }),
    );

    this.started = true;
  }

  dispose(): void {
    if (this.irTimer) clearTimeout(this.irTimer);
    this.irTimer = null;
    for (const u of this.unsubs) u();
    this.unsubs.length = 0;
    this.pitchShifter?.dispose();
    this.pitchShifter = null;
    try {
      this.inputNode?.disconnect();
      this.outputMixNode?.disconnect();
      this.reverbIn?.disconnect();
      this.convolver?.disconnect();
      this.dampFilter?.disconnect();
      this.dryGain?.disconnect();
      this.wetGain?.disconnect();
      this.shimmerGain?.disconnect();
    } catch {
      /* already disconnected */
    }
    this.inputNode = null;
    this.outputMixNode = null;
    this.reverbIn = null;
    this.convolver = null;
    this.dampFilter = null;
    this.dryGain = null;
    this.wetGain = null;
    this.shimmerGain = null;
    this.started = false;
  }

  /** Mod-matrix hookup — Damp's biquad frequency is the only AudioParam we
   *  expose. Size swaps buffers (not an AudioParam), Mix is a crossfade. */
  getAudioParam(paramName: string): AudioParam | null {
    if (paramName === 'Damp') return this.dampFilter?.frequency ?? null;
    return null;
  }

  serialize(): ModuleState {
    return {
      id: this.id,
      type: this.type,
      bypass: this.bypass,
      params: writeParamState(this.parameters),
    };
  }

  deserialize(state: ModuleState): void {
    readParamState(this.parameters, state.params, 'preset');
  }

  // ── Internals ──────────────────────────────────────────────────────────

  /**
   * Equal-ish crossfade between dry and wet. Keeping it linear matches the
   * rest of the FX chain — users who want a pure wet signal just crank
   * Mix to 1, and perfect-equal-power isn't audibly better here.
   */
  private applyMix(mix: number, immediate = false): void {
    if (!this.dryGain || !this.wetGain || !this.engine) return;
    const ctx = this.engine.context;
    const t = ctx.currentTime;
    const dry = Math.max(0, Math.min(1, 1 - mix));
    const wet = Math.max(0, Math.min(1, mix));
    if (immediate) {
      this.dryGain.gain.setValueAtTime(dry, t);
      this.wetGain.gain.setValueAtTime(wet, t);
    } else {
      this.dryGain.gain.cancelScheduledValues(t);
      this.dryGain.gain.setTargetAtTime(dry, t, 0.01);
      this.wetGain.gain.cancelScheduledValues(t);
      this.wetGain.gain.setTargetAtTime(wet, t, 0.01);
    }
  }

  /**
   * Open or close the shimmer regen path based on Pitch. At Pitch=0
   * we mute the path entirely so a vanilla reverb has no extra
   * coloration; non-zero opens the path to SHIMMER_FEEDBACK so the
   * pitched signal cycles through the IR. Smoothed so toggling on
   * a held tail doesn't click.
   */
  private applyShimmerGain(pitch: number, immediate = false): void {
    if (!this.shimmerGain || !this.engine) return;
    const target = pitch === 0 ? 0 : SHIMMER_FEEDBACK;
    const t = this.engine.context.currentTime;
    if (immediate) {
      this.shimmerGain.gain.setValueAtTime(target, t);
    } else {
      this.shimmerGain.gain.cancelScheduledValues(t);
      this.shimmerGain.gain.setTargetAtTime(target, t, 0.05);
    }
  }

  private scheduleIRRegen(): void {
    if (this.irTimer) clearTimeout(this.irTimer);
    this.irTimer = setTimeout(() => {
      this.irTimer = null;
      this.regenerateIR();
    }, IR_DEBOUNCE_MS);
  }

  /**
   * Build a stereo IR of decaying noise. Gives a synthetic but musical
   * reverb — not "cathedral accurate", but tunable and cheap. The decay
   * curve is exponential with a small linear-fade tail so the end doesn't
   * sound like an abrupt cutoff.
   */
  private regenerateIR(): void {
    if (!this.convolver || !this.engine) return;
    const ctx = this.engine.context;
    const sampleRate = ctx.sampleRate;
    const size = Math.min(MAX_SIZE_SEC, Math.max(0.05, this.size.value));
    const length = Math.max(1, Math.floor(sampleRate * size));
    const ir = ctx.createBuffer(2, length, sampleRate);
    for (let ch = 0; ch < 2; ch++) {
      const data = ir.getChannelData(ch);
      // Slightly decorrelate the two channels so the tail has a sense of
      // width. Not true stereo reverb, but better than mono-in-both-ears.
      const seed = ch === 0 ? 0.73 : 1.27;
      for (let i = 0; i < length; i++) {
        // Exponential decay with a soft fade over the last 5% so the IR
        // doesn't terminate on a click. `size` directly controls the
        // decay constant — larger Size → longer, fuller tails.
        const t = i / length;
        const fade = t > 0.95 ? (1 - t) / 0.05 : 1;
        const env = Math.exp(-6 * t) * fade;
        data[i] = (Math.random() * 2 - 1) * env * seed;
      }
    }
    try {
      this.convolver.buffer = ir;
    } catch {
      /* shouldn't happen — but if the shim rejects the buffer, fail quietly */
    }
  }
}
